import React from 'react';
import { isMobile } from 'react-device-detect';
import { usePreferences } from './Preferences/PreferencesContext';
import { getThemeColors } from '../helpers/themeColors';
import NavigationBar from './Shared/NavigationBar';

const SPACING = {
  containerMaxWidth: 800,
  containerPaddingDesktop: 40,
  containerPaddingMobile: 16,
  sectionMarginBottom: 40,
  headingMarginBottom: 12,
  paragraphMarginBottom: 8,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  heading: 18,
  body: 15,
  bodyLineHeight: 1.7,
  lastUpdated: 13,
};

const buildColors = (theme) => {
  const t = getThemeColors(theme);
  return {
    text: t.textPrimary,
    body: t.textSecondary,
    link: t.accentPrimary,
    divider: t.borderPrimary,
    muted: t.textFaint,
  };
};

const containerStyle = {
  maxWidth: SPACING.containerMaxWidth,
  margin: '0 auto',
  padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPaddingDesktop,
};

const sectionStyle = {
  marginBottom: SPACING.sectionMarginBottom,
};

// Last section omits bottom margin so the footer sits flush
const lastSectionStyle = {};

const LAST_UPDATED = 'April 10, 2026';

const PrivacyPolicy = () => {
  const { preferences } = usePreferences();
  const COLORS = buildColors(preferences.theme);

  const titleStyle = {
    fontSize: isMobile ? FONT.titleMobile : FONT.title,
    color: COLORS.text,
    marginBottom: SPACING.paragraphMarginBottom,
  };

  const lastUpdatedStyle = {
    fontSize: FONT.lastUpdated,
    color: COLORS.muted,
    marginBottom: SPACING.sectionMarginBottom,
  };

  const headingStyle = {
    fontSize: FONT.heading,
    color: COLORS.text,
    marginBottom: SPACING.headingMarginBottom,
    paddingBottom: SPACING.paragraphMarginBottom,
    borderBottom: `1px solid ${COLORS.divider}`,
  };

  const bodyStyle = {
    fontSize: FONT.body,
    color: COLORS.body,
    lineHeight: FONT.bodyLineHeight,
    marginBottom: SPACING.paragraphMarginBottom,
  };

  const linkStyle = {
    color: COLORS.link,
  };

  return (
    <>
      <NavigationBar />
      <div style={containerStyle}>
        <h1 style={titleStyle}>Privacy Policy</h1>
        <p style={lastUpdatedStyle}>Last updated: {LAST_UPDATED}</p>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>What We Collect</h2>
          <p style={bodyStyle}>
            When you create an account, CollectNDeck collects the following personal information:
          </p>
          <p style={bodyStyle}>
            <strong>Email address:</strong> Used to identify your account and send
            password-reset emails.
          </p>
          <p style={bodyStyle}>
            <strong>Password:</strong> Stored as a hashed value using a one-way hashing
            algorithm. Your plaintext password is never stored.
          </p>
          <p style={bodyStyle}>
            <strong>Collection data:</strong> The card entries, quantities, conditions, and deck
            lists you add to your account. This data is stored on your behalf and is not shared.
          </p>
          <p style={bodyStyle}>
            <strong>Session token:</strong> When you log in, a JSON Web Token (JWT) is stored
            in your browser to maintain your session. This token is used solely for authentication
            and is removed when you log out.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>How We Use It</h2>
          <p style={bodyStyle}>
            Your data is used solely to operate your account and store your collection. Specifically:
          </p>
          <p style={bodyStyle}>
            <strong>Authentication:</strong> Your email and hashed password are used to verify
            your identity when you log in.
          </p>
          <p style={bodyStyle}>
            <strong>Collection storage:</strong> Your card and deck data is stored so it persists
            between sessions and is available when you return.
          </p>
          <p style={bodyStyle}>
            CollectNDeck does not sell, rent, or share your personal information with third parties
            for marketing or any other purpose.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Third-Party Services</h2>
          <p style={bodyStyle}>
            CollectNDeck uses the following third-party services in its operation:
          </p>
          <p style={bodyStyle}>
            <a href="https://scryfall.com" target="_blank" rel="noreferrer" style={linkStyle}>
              <strong>Scryfall</strong>
            </a>{' '}
            provides card data, prices, and images displayed in the app. Your search queries are
            sent to Scryfall but are not associated with your account or personal information.
          </p>
          <p style={bodyStyle}>
            <strong>Amazon Web Services (AWS):</strong> CollectNDeck is hosted on AWS
            infrastructure in the United States. Your account data resides on AWS servers located
            in the US. If you are accessing CollectNDeck from outside the United States, your data
            will be transferred to and processed in the US. AWS is subject to its own{' '}
            <a
              href="https://aws.amazon.com/privacy/"
              target="_blank"
              rel="noreferrer"
              style={linkStyle}
            >
              privacy policy
            </a>
            .
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Data Retention</h2>
          <p style={bodyStyle}>
            Your account and all associated collection data is retained for as long as your account
            is active. If you would like your data deleted, contact us at the address below and we
            will remove your account and all associated data within 30 days.
          </p>
          <p style={bodyStyle}>
            CollectNDeck does not retain any data beyond what is necessary to operate your account.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Your Rights</h2>
          <p style={bodyStyle}>
            You have the right to access, correct, and delete the personal data we hold about you.
            Specifically:
          </p>
          <p style={bodyStyle}>
            <strong>Access:</strong> You may request a copy of the personal data we hold on your
            account.
          </p>
          <p style={bodyStyle}>
            <strong>Correction:</strong> If any information we hold is inaccurate, you may
            request that it be corrected.
          </p>
          <p style={bodyStyle}>
            <strong>Deletion:</strong> You may request that your account and all associated data
            be permanently deleted.
          </p>
          <p style={bodyStyle}>
            To exercise any of these rights, contact us using the information in the Contact section
            below. We will fulfill all requests within 30 days.
          </p>
        </div>

        <div style={sectionStyle}>
          <h2 style={headingStyle}>Data Security</h2>
          <p style={bodyStyle}>
            All data transmitted between your browser and CollectNDeck is encrypted in transit
            via HTTPS. Passwords are hashed using a one-way algorithm before storage. Your
            plaintext password is never written to disk or logged. Session tokens are scoped to
            your account and expire on logout.
          </p>
          <p style={bodyStyle}>
            While we take reasonable precautions to protect your data, no system is completely
            secure. We encourage you to use a strong, unique password for your account.
          </p>
        </div>

        <div style={lastSectionStyle}>
          <h2 style={headingStyle}>Contact</h2>
          <p style={bodyStyle}>
            For privacy-related questions, data access requests, or deletion requests, please
            contact us at:
          </p>
          <p style={bodyStyle}>
            <strong>Email:</strong>{' '}
            <a href="mailto:collectndeck@gmail.com" style={linkStyle}>
              collectndeck@gmail.com
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
